// src/components/Recenzie.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';

const Section = styled.section`
    padding: 60px 0;
    background: #eeeeee;
    position: relative;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const HeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 3rem;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 1.5rem;
        text-align: center;
    }
`;

const LeftHeader = styled.div`
    flex: 1;
    padding-right: 2rem;

    @media (max-width: 768px) {
        padding-right: 0;
    }
`;

const SectionLabel = styled.p`
    font-size: 0.9rem;
    color: #990147;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    font-weight: 600;
`;

const SectionTitle = styled(motion.h2)`
    font-size: 2.5rem;
    font-weight: 800;
    color: #000000;
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Divider = styled(motion.div)`
    width: 60px;
    height: 3px;
    background: #990147;
    margin-top: 1rem;

    @media (max-width: 768px) {
        margin: 1rem auto 0;
    }
`;

const RightDescription = styled.p`
    flex: 1;
    font-size: 1rem;
    color: #6b7280;
    line-height: 1.6;
    max-width: 400px;

    @media (max-width: 768px) {
        max-width: none;
    }
`;

const SwiperContainer = styled.div`
    position: relative;
    margin-bottom: 2rem;

    &::before,
    &::after {
        content: '';
        position: absolute;
        top: 0;
        height: 100%;
        width: 100px;
        z-index: 2;
        pointer-events: none;
    }

    &::before {
        left: 0;
        background: #eeeeee;
    }

    &::after {
        right: 0;
        background: #eeeeee;
    }

    .swiper {
        padding: 0 50px;
    }

    @media (max-width: 768px) {
        &::before,
        &::after {
            width: 50px;
        }

        .swiper {
            padding: 0 20px;
        }
    }
`;

const ReviewCard = styled(motion.div)`
    background: #ffffff;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Stars = styled.div`
    color: #f59e0b;
    margin-bottom: 1rem;
`;

const ReviewContent = styled.p`
    font-size: 0.95rem;
    color: #4b5563;
    line-height: 1.5;
    margin-bottom: 1rem;
`;

const ReviewerName = styled.p`
    font-size: 0.9rem;
    color: #6b7280;
    font-weight: 500;
`;

const ReviewerDate = styled.span`
    display: block;
    font-size: 0.85rem;
    color: #9ca3af;
`;

const ReviewLinks = styled.div`
    text-align: center;
    margin-top: 2rem;
    display: flex;
    justify-content: center;
    gap: 1.5rem;
`;

const ReviewLinkButton = styled(motion.a)`
    padding: 0.6rem 1.5rem;
    background: linear-gradient(135deg, #990147, #990147);
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.05);
    }
`;

const StatsContainer = styled(motion.div)`
    background: linear-gradient(135deg,
    rgba(153, 1, 71, 0.05) 0%,
    rgba(153, 1, 71, 0.05) 100%);
    border-radius: 16px;
    padding: 2rem;
    text-align: center;
    border: 1px solid rgba(153, 1, 71, 0.1);
    margin-top: 2rem;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const StatItem = styled.div`
    text-align: center;
`;

const StatNumber = styled.div`
    font-size: 2rem;
    font-weight: 800;
    background: linear-gradient(135deg, #990147, #990147);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.4rem;
`;

const StatLabel = styled.div`
    color: #4a5568;
    font-weight: 600;
    font-size: 0.85rem;
`;

const StatStars = styled.div`
    color: #f59e0b;
    font-size: 1.5rem;
    margin-bottom: 0.4rem;
`;

const FloatingElements = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
`;

const FloatingElement = styled(motion.div)`
    position: absolute;
    background: linear-gradient(135deg, #990147, #990147);
    border-radius: 12px;
    opacity: 0.1;

    &.element-1 {
        width: 80px;
        height: 80px;
        top: 10%;
        right: 5%;
    }

    &.element-2 {
        width: 50px;
        height: 50px;
        bottom: 20%;
        left: 3%;
    }
`;

interface Review {
    id: number;
    name: string;
    location: string;
    date: string;
    rating: number;
    content: string;
    verified: boolean;
}

const Recenzie: React.FC = () => {
    const reviews: Review[] = [
        {
            id: 1,
            name: "Ján Haluška",
            location: "",
            date: "26. máj 2020",
            rating: 5,
            content: "Ak by sa dalo slovami opísať, ako mi vľúdnym hlasom poradila, vysvetlila postup, opísala charakteristické črty a nakoniec zapriala aby sa mi darilo, tak slovami sa opísať nedá. Pani doktorka, aj napriek tomu, že nemala veľa času, tak ma neodbila ale snažila sa čo najpodrobnejšie vysvetliť, to ma v živote nevidela a rozhovor prebiehal len cez telefón. Všetká česť pani doktorka",
            verified: true
        },
        {
            id: 2,
            name: "Lenka Franeková",
            location: "",
            date: "26. jún 2019",
            rating: 5,
            content: "Ked pocuvam odbornika, ktory sa vyjadruje odborne, no zaroven tak aby mu rozumel aj obycajny lud ked to ma hlavu a patu. a zatial vsetko co som si vypocula ma zaujalo a porozumela som tomu. Sonicka, som rada, ze si sa priblizila k nam takouto formou a verim, ze tvoja stranka bude prospešná pre mnohych.",
            verified: true
        },
        {
            id: 3,
            name: "Juraj Mihálik",
            location: "",
            date: "7. január 2023",
            rating: 5,
            content: "Pani doktorka je veľmi milá a vzdelaná. Výborne mi pomohla. Odporúčam aj videá na YouTube. Ďakujem veľmi pekne!",
            verified: true
        },
        {
            id: 4,
            name: "Martin Glogovský",
            location: "",
            date: "pred rokom",
            rating: 5,
            content: "Veľmi profesionálny a empatický prístup. Pani doktorka bola veľmi milá, chápavá a pokojná. Počúvala ma s pozornosťou a poskytla mi cenné rady a podporu. Určite odporúčam každému, kto hľadá odbornú a zároveň ľudskú pomoc. 5/5",
            verified: true
        }
    ];

    return (
        <Section id="recenzie">
            <FloatingElements>
                <FloatingElement
                    className="element-1"
                    animate={{
                        y: [0, -30, 0],
                        rotate: [0, 180, 360]
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <FloatingElement
                    className="element-2"
                    animate={{
                        y: [0, 20, 0],
                        rotate: [0, -90, 0]
                    }}
                    transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                />
            </FloatingElements>

            <Container>
                <HeaderWrapper>
                    <LeftHeader>
                        <SectionLabel>Recenzie</SectionLabel>
                        <SectionTitle
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            Dôveruje nám
                            200+ pacientov
                        </SectionTitle>
                        <Divider
                            initial={{ width: 0 }}
                            whileInView={{ width: 60 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                        />
                    </LeftHeader>
                    <RightDescription>
                        Či ste nový pacient alebo dlhoročný, nechajte nás byť vaším dôveryhodným sprievodcom na ceste k lepšiemu duševnému zdraviu.
                    </RightDescription>
                </HeaderWrapper>

                <SwiperContainer>
                    <Swiper
                        modules={[Autoplay, Navigation, EffectCoverflow]}
                        spaceBetween={20}
                        slidesPerView={3}
                        centeredSlides={true}
                        loop={true}
                        autoplay={{
                            delay: 0,
                            disableOnInteraction: false,
                            reverseDirection: true,
                            pauseOnMouseEnter: true
                        }}
                        speed={5000}
                        effect="coverflow"
                        coverflowEffect={{
                            rotate: 0,
                            stretch: 0,
                            depth: 100,
                            modifier: 2.5,
                            slideShadows: false
                        }}
                        breakpoints={{
                            320: {
                                slidesPerView: 1,
                                spaceBetween: 10
                            },
                            768: {
                                slidesPerView: 2,
                                spaceBetween: 15
                            },
                            1024: {
                                slidesPerView: 3,
                                spaceBetween: 20
                            }
                        }}
                    >
                        {reviews.map((review) => (
                            <SwiperSlide key={review.id}>
                                <ReviewCard
                                    whileHover={{ y: -5 }}
                                >
                                    <Stars>★★★★★</Stars>
                                    <ReviewContent>{review.content}</ReviewContent>
                                    <ReviewerName><strong>{review.name}</strong></ReviewerName>
                                    <ReviewerDate>{review.date}</ReviewerDate>
                                </ReviewCard>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </SwiperContainer>

                <ReviewLinks>
                    <ReviewLinkButton
                        href="https://www.facebook.com/belanskasona/reviews"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Recenzie na Facebooku
                    </ReviewLinkButton>
                    <ReviewLinkButton
                        href="https://www.google.com/search?q=MUDr.+So%C5%88a+Belansk%C3%A1+psychiatri%C4%8Dka+Bratislava"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Recenzie na Google
                    </ReviewLinkButton>
                </ReviewLinks>

                <StatsContainer
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <StatsGrid>
                        <StatItem>
                            <StatNumber>5.0</StatNumber>

                            <StatStars>★★★★★</StatStars>
                            <StatLabel>Priemerné Hodnotenie</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>200+</StatNumber>
                            <StatLabel>Spokojných Pacientov</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>98%</StatNumber>
                            <StatLabel>By Odporučilo</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>10+</StatNumber>
                            <StatLabel>5-Hviezdičkových Recenzií</StatLabel>
                        </StatItem>
                    </StatsGrid>
                </StatsContainer>
            </Container>
        </Section>
    );
};

export default Recenzie;